import React, { useState, useEffect } from 'react';
import { ArrowLeft, TrendingUp, Shield, PieChart, DollarSign, Users, Target, CheckCircle } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Slider } from '../components/ui/slider';

const MutualFundsPage = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  
  // SIP Calculator State
  const [sipAmount, setSipAmount] = useState(10000);
  const [annualReturn, setAnnualReturn] = useState(12);
  const [investmentYears, setInvestmentYears] = useState(10);
  const [inflationRate, setInflationRate] = useState(6);

  // FD vs MF Comparison Calculator State
  const [comparisonAmount, setComparisonAmount] = useState(1000000);
  const [comparisonYears, setComparisonYears] = useState(3);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // SIP Calculator Functions
  const calculateSIP = () => {
    const monthlyRate = annualReturn / 100 / 12;
    const months = investmentYears * 12;
    const totalInvestment = sipAmount * months;
    
    if (monthlyRate === 0) {
      return {
        totalInvestment,
        maturityAmount: totalInvestment,
        returns: 0
      };
    }
    
    const maturityAmount = sipAmount * (((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) * (1 + monthlyRate));
    const returns = maturityAmount - totalInvestment;
    
    return {
      totalInvestment,
      maturityAmount,
      returns
    };
  };

  const calculateInflationAdjusted = () => {
    const results = calculateSIP();
    const inflationAdjustedValue = results.maturityAmount / Math.pow(1 + inflationRate / 100, investmentYears);
    return {
      ...results,
      inflationAdjustedValue
    };
  };

  // FD vs MF Comparison Calculator Functions
  const calculateComparison = () => {
    // FD calculation (simple interest, taxed at 30%)
    const fdRate = 5.75; // 5.75% FD rate
    const fdInterest = comparisonAmount * (fdRate / 100) * comparisonYears;
    const fdTax = fdInterest * 0.30; // 30% tax bracket
    const fdReturns = comparisonAmount + fdInterest - fdTax;
    
    // Debt MF calculation (with cost indexation benefit)
    const mfRate = 7.77; // 7.77% MF rate
    const mfInterest = comparisonAmount * (mfRate / 100) * comparisonYears;
    const mfTax = mfInterest * 0.20; // 20% with cost indexation benefit
    const mfReturns = comparisonAmount + mfInterest - mfTax;
    
    const taxSavings = fdTax - mfTax;
    
    return {
      fdReturns: Math.round(fdReturns),
      mfReturns: Math.round(mfReturns),
      taxSavings: Math.round(taxSavings)
    };
  };

  // Contact form scroll function
  const scrollToContactForm = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Handle button actions based on device
  const handleScheduleConsultation = () => {
    if (isMobile) {
      window.location.href = 'tel:+919904449922';
    } else {
      navigate('/#contact');
    }
  };

  const handleGetProposal = () => {
    window.location.href = 'mailto:nitumf2017@gmail.com';
  };

  const advantages = [
    {
      icon: <DollarSign size={32} />,
      title: "Simplicity",
      description: "Buying a mutual fund is easy! The minimum investment is very small. As little as ₹500 can be invested on a monthly basis through SIP."
    },
    {
      icon: <TrendingUp size={32} />,
      title: "Economies of Scale",
      description: "Because mutual funds buy and sell large amounts of securities at a time, transaction costs are lower than individual investments."
    },
    {
      icon: <PieChart size={32} />,
      title: "Diversification",
      description: "By owning units in a mutual fund, your risk is spread out across multiple stocks and bonds, minimizing individual investment risk."
    },
    {
      icon: <Users size={32} />,
      title: "Professional Management",
      description: "Get full-time professional fund managers to make and monitor investments on your behalf with expertise and experience."
    }
  ];

  const investmentTypes = [
    {
      title: "Equity Funds",
      description: "Invest primarily in stocks for long-term wealth creation",
      features: ["High growth potential", "Suitable for long-term investors", "Market-linked returns"]
    },
    {
      title: "Debt Funds",
      description: "Invest in bonds and government securities for steady income",
      features: ["Stable returns", "Lower risk", "Regular income generation"]
    },
    {
      title: "Hybrid Funds",
      description: "Balanced allocation between equity and debt securities",
      features: ["Balanced risk-return", "Professional asset allocation", "Suitable for moderate investors"]
    },
    {
      title: "Tax Saving Funds (ELSS)",
      description: "Equity funds with tax benefits under Section 80C",
      features: ["Tax deduction up to ₹1.5 lakhs", "3-year lock-in period", "Wealth creation with tax savings"]
    }
  ];

  const sipBenefits = [
    "Start with as low as ₹500 per month",
    "Rupee cost averaging benefits",
    "Disciplined investment approach",
    "Flexibility to increase or pause",
    "Compounding benefits over time",
    "No market timing required"
  ];

  const calculatorResults = calculateInflationAdjusted();

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Breadcrumb Navigation */}
      <div className="bg-light-gold py-4">
        <div className="container">
          <div className="flex items-center gap-3 text-sm">
            <Link to="/" className="text-primary-green hover:text-primary-gold transition-colors">
              Home
            </Link>
            <span className="text-medium-gray">/</span>
            <span className="text-primary-green font-medium">Mutual Funds</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="section-padding bg-gradient-hero">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Link 
                to="/" 
                className="btn-secondary inline-flex items-center gap-2 hover:scale-105 transition-transform"
              >
                <ArrowLeft size={18} />
                Back to Home
              </Link>
            </div>
            
            <div className="text-primary-gold mb-4">
              <TrendingUp size={64} className="mx-auto" />
            </div>
            
            <h1 className="heading-hero mb-6">
              Mutual Fund <span className="text-primary-gold">Investing</span>
            </h1>
            
            <p className="text-large mb-8 max-w-3xl mx-auto">
              Pool your investments professionally, diversify effortlessly, and leave the portfolio management to us—so you can focus on your life goals.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <button 
                onClick={() => {
                  setTimeout(() => {
                    document.getElementById('contact-section').scrollIntoView({ 
                      behavior: 'smooth',
                      block: 'start'
                    });
                  }, 100);
                }}
                className="btn-primary"
              >
                <Target size={20} />
                Start Investing Today
              </button>
              <button 
                onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
                className="btn-secondary"
              >
                Learn How It Works
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* What is Mutual Fund Section */}
      <section className="section-padding">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="fade-in-up">
              <h2 className="heading-section mb-6">What is a Mutual Fund?</h2>
              <div className="space-y-4 text-body">
                <p>
                  A mutual fund is simply a financial intermediary that allows a group of investors to pool their money together with a predetermined investment objective.
                </p>
                <p>
                  The mutual fund will have a fund manager who is responsible for investing the pooled money into specific securities (usually stocks or bonds).
                </p>
                <p>
                  Mutual funds are one of the best investments ever created because they are very cost efficient and very easy to invest in - you don't have to figure out which stocks or bonds to buy.
                </p>
              </div>
            </div>
            
            <div className="fade-in-up delay-2">
              <div className="bg-cream-bg rounded-3xl p-8 border-2 border-primary-gold border-opacity-20">
                <h3 className="heading-card mb-6 text-center">Key Benefits</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-white rounded-xl">
                    <div className="text-2xl font-bold text-primary-gold font-poppins mb-2">₹500</div>
                    <div className="text-sm text-secondary">Minimum SIP</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-xl">
                    <div className="text-2xl font-bold text-primary-gold font-poppins mb-2">15%+</div>
                    <div className="text-sm text-secondary">Avg Annual Returns*</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-xl">
                    <div className="text-2xl font-bold text-primary-gold font-poppins mb-2">50+</div>
                    <div className="text-sm text-secondary">Fund Options</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-xl">
                    <div className="text-2xl font-bold text-primary-gold font-poppins mb-2">24/7</div>
                    <div className="text-sm text-secondary">Online Access</div>
                  </div>
                </div>
                <p className="text-xs text-secondary mt-4 text-center">
                  *Past performance is not indicative of future results
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section id="how-it-works" className="section-padding bg-gradient-section">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="heading-section mb-6">How Mutual Funds Work</h2>
            <p className="text-large max-w-3xl mx-auto">
              Understanding the simple process behind mutual fund investments
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center fade-in-up">
                <div className="w-16 h-16 bg-primary-gold rounded-full flex items-center justify-center text-white text-xl font-bold mx-auto mb-4">
                  1
                </div>
                <h3 className="heading-card mb-4">Pool Your Money</h3>
                <p className="text-body">
                  Multiple investors contribute money to create a large investment pool managed by professionals.
                </p>
              </div>
              
              <div className="text-center fade-in-up delay-1">
                <div className="w-16 h-16 bg-primary-gold rounded-full flex items-center justify-center text-white text-xl font-bold mx-auto mb-4">
                  2
                </div>
                <h3 className="heading-card mb-4">Professional Management</h3>
                <p className="text-body">
                  Expert fund managers invest this pooled money in diverse securities based on the fund's objective.
                </p>
              </div>
              
              <div className="text-center fade-in-up delay-2">
                <div className="w-16 h-16 bg-primary-gold rounded-full flex items-center justify-center text-white text-xl font-bold mx-auto mb-4">
                  3
                </div>
                <h3 className="heading-card mb-4">Share the Returns</h3>
                <p className="text-body">
                  Profits or losses are distributed among investors proportional to their investment amount.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="section-padding">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="heading-section mb-6">Advantages of Mutual Funds</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {advantages.map((advantage, index) => (
              <div key={index} className="service-card fade-in-up" style={{animationDelay: `${index * 0.2}s`}}>
                <div className="text-primary-gold mb-4">
                  {advantage.icon}
                </div>
                <h3 className="heading-card mb-4">{advantage.title}</h3>
                <p className="text-body">{advantage.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Types of Mutual Funds */}
      <section className="section-padding bg-gradient-section">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="heading-section mb-6">Types of Mutual Funds</h2>
            <p className="text-large max-w-3xl mx-auto">
              Choose from a variety of mutual fund categories based on your investment goals and risk appetite
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {investmentTypes.map((type, index) => (
              <div key={index} className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 fade-in-up" style={{animationDelay: `${index * 0.2}s`}}>
                <h3 className="heading-card mb-4">{type.title}</h3>
                <p className="text-body mb-6">{type.description}</p>
                <ul className="space-y-3">
                  {type.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start gap-3">
                      <CheckCircle size={16} className="text-primary-gold mt-1 flex-shrink-0" />
                      <span className="text-body text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive SIP Calculator Section */}
      <section className="section-padding">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="fade-in-up">
              <h2 className="heading-section mb-6">Systematic Investment Plan (SIP)</h2>
              <p className="text-body mb-6">
                SIP is a smart investment strategy that allows you to invest a fixed amount regularly in mutual funds. It's the most disciplined way to create wealth over time.
              </p>
              
              <div className="space-y-4">
                {sipBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle size={18} className="text-primary-gold mt-1 flex-shrink-0" />
                    <span className="text-body">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="fade-in-up delay-2">
              <div className="bg-primary-green rounded-3xl p-8 text-white">
                <h3 className="text-2xl font-poppins font-semibold mb-6 text-center">
                  Interactive SIP Calculator
                </h3>
                
                {/* SIP Amount */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-sm font-medium">Monthly SIP Amount</label>
                    <span className="text-primary-gold font-semibold">₹{sipAmount.toLocaleString()}</span>
                  </div>
                  <Slider
                    value={[sipAmount]}
                    onValueChange={(value) => setSipAmount(value[0])}
                    min={500}
                    max={100000}
                    step={500}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs mt-1 opacity-75">
                    <span>₹500</span>
                    <span>₹1,00,000</span>
                  </div>
                </div>

                {/* Annual Return */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-sm font-medium">Expected Annual Return</label>
                    <span className="text-primary-gold font-semibold">{annualReturn}%</span>
                  </div>
                  <Slider
                    value={[annualReturn]}
                    onValueChange={(value) => setAnnualReturn(value[0])}
                    min={1}
                    max={30}
                    step={0.5}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs mt-1 opacity-75">
                    <span>1%</span>
                    <span>30%</span>
                  </div>
                </div>

                {/* Investment Period */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-sm font-medium">Investment Period</label>
                    <span className="text-primary-gold font-semibold">{investmentYears} years</span>
                  </div>
                  <Slider
                    value={[investmentYears]}
                    onValueChange={(value) => setInvestmentYears(value[0])}
                    min={1}
                    max={40}
                    step={1}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs mt-1 opacity-75">
                    <span>1 year</span>
                    <span>40 years</span>
                  </div>
                </div>

                {/* Inflation Rate */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-sm font-medium">Inflation Rate</label>
                    <span className="text-primary-gold font-semibold">{inflationRate}%</span>
                  </div>
                  <Slider
                    value={[inflationRate]}
                    onValueChange={(value) => setInflationRate(value[0])}
                    min={0}
                    max={15}
                    step={0.5}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs mt-1 opacity-75">
                    <span>0%</span>
                    <span>15%</span>
                  </div>
                </div>

                {/* Results */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-white bg-opacity-10 rounded-xl p-4 text-center">
                    <div className="text-lg font-bold text-primary-gold">
                      ₹{Math.round(calculatorResults.totalInvestment / 100000)}L
                    </div>
                    <div className="text-xs opacity-90">Total Investment</div>
                  </div>
                  <div className="bg-white bg-opacity-10 rounded-xl p-4 text-center">
                    <div className="text-lg font-bold text-primary-gold">
                      ₹{Math.round(calculatorResults.maturityAmount / 100000)}L
                    </div>
                    <div className="text-xs opacity-90">Maturity Value</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-white bg-opacity-10 rounded-xl p-4 text-center">
                    <div className="text-lg font-bold text-primary-gold">
                      ₹{Math.round(calculatorResults.returns / 100000)}L
                    </div>
                    <div className="text-xs opacity-90">Wealth Gained</div>
                  </div>
                  <div className="bg-white bg-opacity-10 rounded-xl p-4 text-center">
                    <div className="text-lg font-bold text-primary-gold">
                      ₹{Math.round(calculatorResults.inflationAdjustedValue / 100000)}L
                    </div>
                    <div className="text-xs opacity-90">Real Value Today</div>
                  </div>
                </div>

                <p className="text-xs opacity-75 text-center">
                  *This is for illustration purposes only. Past performance does not guarantee future results.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FD vs MF Comparison Calculator Section */}
      <section className="section-padding bg-gradient-section">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="fade-in-up">
              <h2 className="heading-section mb-6">Comparison with FD for the Same Investment & Tenure</h2>
              <p className="text-body mb-6">
                See how Debt Mutual Funds can provide better returns than Fixed Deposits due to tax advantages and cost indexation benefits.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle size={18} className="text-primary-gold mt-1 flex-shrink-0" />
                  <span className="text-body">Cost Indexation benefit reduces tax liability</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle size={18} className="text-primary-gold mt-1 flex-shrink-0" />
                  <span className="text-body">Higher returns with professional management</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle size={18} className="text-primary-gold mt-1 flex-shrink-0" />
                  <span className="text-body">Tax savings even with same rate of return</span>
                </div>
              </div>
            </div>
            
            <div className="fade-in-up delay-2">
              <div className="bg-primary-green rounded-3xl p-8 text-white">
                <h3 className="text-2xl font-poppins font-semibold mb-6 text-center">
                  FD vs Debt MF Calculator
                </h3>
                
                {/* Investment Amount */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-sm font-medium">Select Amount</label>
                    <span className="text-primary-gold font-semibold">₹{comparisonAmount.toLocaleString('en-IN')}</span>
                  </div>
                  <Slider
                    value={[comparisonAmount]}
                    onValueChange={(value) => setComparisonAmount(value[0])}
                    min={100000}
                    max={10000000}
                    step={100000}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs mt-1 opacity-75">
                    <span>₹1,00,000</span>
                    <span>₹1 Crore</span>
                  </div>
                </div>

                {/* Investment Period */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-sm font-medium">Select Year</label>
                    <span className="text-primary-gold font-semibold">{comparisonYears} Yrs</span>
                  </div>
                  <Slider
                    value={[comparisonYears]}
                    onValueChange={(value) => setComparisonYears(value[0])}
                    min={3}
                    max={10}
                    step={1}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs mt-1 opacity-75">
                    <span>03 Yr</span>
                    <span>10 Yrs</span>
                  </div>
                </div>

                {/* Results */}
                {(() => {
                  const results = calculateComparison();
                  return (
                    <>
                      <div className="grid grid-cols-2 gap-4 mb-6">
                        <div className="bg-white bg-opacity-20 rounded-xl p-4 text-center">
                          <div className="text-lg font-bold text-primary-gold mb-1">
                            ₹{results.mfReturns.toLocaleString('en-IN')}
                          </div>
                          <div className="text-xs opacity-90">Debt MF Returns</div>
                          <div className="text-xs opacity-75">(7.77%)*</div>
                        </div>
                        <div className="bg-white bg-opacity-20 rounded-xl p-4 text-center">
                          <div className="text-lg font-bold text-primary-gold mb-1">
                            ₹{results.fdReturns.toLocaleString('en-IN')}
                          </div>
                          <div className="text-xs opacity-90">FD Returns</div>
                          <div className="text-xs opacity-75">(5.75%)*</div>
                        </div>
                      </div>
                      
                      <div className="bg-white bg-opacity-20 rounded-xl p-4 text-center mb-4">
                        <div className="text-lg font-bold text-primary-gold mb-1">
                          ₹{results.taxSavings.toLocaleString('en-IN')}
                        </div>
                        <div className="text-xs opacity-90">Net Savings in Tax Payment</div>
                        <div className="text-xs opacity-75">(for not investing in FD)</div>
                      </div>
                      
                      <div className="text-xs opacity-75 text-center space-y-1">
                        <div>Assumption: Rate of Return/Interest assumed @8%</div>

                        <div>Investor Under Highest Tax Bracket - 30%</div>
                        <div>Debt MF will attract Cost Indexation benefit of @20%</div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact-section" className="section-padding bg-light-gold">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="heading-section mb-6">Ready to Start Your Investment Journey?</h2>
            <p className="text-large mb-8">
              Get personalized mutual fund recommendations based on your financial goals and risk profile.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-2xl p-6 text-center">
                <div className="text-primary-gold mb-3">
                  <Shield size={32} className="mx-auto" />
                </div>
                <h3 className="font-poppins font-semibold text-primary-green mb-2">Expert Guidance</h3>
                <p className="text-body text-sm">Personalized fund selection based on your goals</p>
              </div>
              <div className="bg-white rounded-2xl p-6 text-center">
                <div className="text-primary-gold mb-3">
                  <DollarSign size={32} className="mx-auto" />
                </div>
                <h3 className="font-poppins font-semibold text-primary-green mb-2">Zero Commission</h3>
                <p className="text-body text-sm">Direct plans with no hidden charges</p>
              </div>
              <div className="bg-white rounded-2xl p-6 text-center">
                <div className="text-primary-gold mb-3">
                  <Users size={32} className="mx-auto" />
                </div>
                <h3 className="font-poppins font-semibold text-primary-green mb-2">Ongoing Support</h3>
                <p className="text-body text-sm">Regular portfolio reviews and rebalancing</p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={handleScheduleConsultation}
                className="btn-primary"
              >
                Schedule Free Consultation
              </button>
              {isMobile && (
                <button 
                  onClick={handleGetProposal}
                  className="btn-secondary"
                >
                  Get Investment Proposal
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MutualFundsPage;